import { createIdentifier, Service } from '@toeverything/infra';
import { nanoid } from 'nanoid';

import type { DesktopApiService, TabViewsMetaSchema } from '../../desktop-api';
import type { GlobalStateService } from '../../storage';
import type { ViewIconName } from '../constants';

export type WorkbenchDefaultState = {
  basename: string;
  views: {
    id: string;
    path?: { pathname?: string; hash?: string; search?: string };
    icon?: ViewIconName;
    title?: string;
  }[];
  activeViewIndex: number;
};

export const WorkbenchDefaultState = createIdentifier<WorkbenchDefaultState>(
  'WorkbenchDefaultState'
);

export const InMemoryWorkbenchDefaultState: WorkbenchDefaultState = {
  basename: '/',
  views: [
    {
      id: nanoid(),
    },
  ],
  activeViewIndex: 0,
};

export class DesktopWorkbenchDefaultState
  extends Service
  implements WorkbenchDefaultState
{
  constructor(
    private readonly globalStateService: GlobalStateService,
    private readonly electronApi: DesktopApiService
  ) {
    super();
  }

  get value() {
    const tabViewsMeta =
      this.globalStateService.globalState.get<TabViewsMetaSchema>(
        'tabViewsMetaSchema'
      );
    const rawState =
      tabViewsMeta?.workbenches.find(
        w => w.id === this.electronApi.appInfo.viewId
      ) || InMemoryWorkbenchDefaultState;
    // 仅浏览器环境下注入
    if (typeof window === 'undefined') {
      return rawState;
    }

    // 外层路径：/workspace/:workspaceId/:rest  ->  内层路径：/:rest
    const { pathname, search, hash } = window.location;
    const m = pathname.match(/^\/workspace\/[^/]+(\/.*)$/);
    const innerPathname = m?.[1] || '/home';
    console.log('aaaaaaaaahhhhinnerPathname', m?.[1], innerPathname);
    // 只为激活视图注入，且仅在其 path 为空时
    const views = rawState.views.slice();
    const activeIndex = Math.min(
      Math.max(rawState.activeViewIndex ?? 0, 0),
      views.length - 1
    );

    if (!views[activeIndex]?.path) {
      views[activeIndex] = {
        ...views[activeIndex],
        path: {
          pathname: innerPathname,
          search: search || '',
          hash: hash || '',
        },
      };
    }

    return {
      ...rawState,
      views,
    };
  }

  get basename() {
    return this.value.basename;
  }

  get activeViewIndex() {
    return this.value.activeViewIndex;
  }

  get views() {
    return this.value.views;
  }
}

export class BrowserWorkbenchDefaultState
  extends Service
  implements WorkbenchDefaultState
{
  constructor() {
    super();
  }

  get value(): WorkbenchDefaultState {
    const state = InMemoryWorkbenchDefaultState;

    if (typeof window === 'undefined') {
      return state;
    }

    const { pathname, search, hash } = window.location;
    const match = pathname.match(/^\/workspace\/[^/]+(\/.*)$/);
    const innerPathname = match?.[1] || '/home';

    const views = state.views.map((view, idx) => {
      if (idx !== state.activeViewIndex || view.path) {
        return view;
      }

      return {
        ...view,
        path: {
          pathname: innerPathname,
          search: search || '',
          hash: hash || '',
        },
      };
    });

    return {
      ...state,
      views,
    };
  }

  get basename() {
    return this.value.basename;
  }
  get activeViewIndex() {
    return this.value.activeViewIndex;
  }
  get views() {
    return this.value.views;
  }
}
